<template>
  <div class="friend-table-panel" :style="themeStyle">
    <!--
      时间表视图：把「周导航 + 好友的分类 + 时间表」整块包起来，并给它 min-height:100%。
      这样它一定占满面板的可见高度 —— 时间表拿到全部可用高度（不会被周累计表挤扁），
      周累计表则被推到折线以下，必须向下滑动一段距离才能看到。
      与用户自己的「时间表」页（ListPage）的观感一致：上面一张大表，往下滑才是周累计表。
    -->
    <div class="table-view">
      <div class="table-head">
        <WeekNavigator
          :week-start="weekStart"
          @change-week="onChangeWeek"
          @go-today="onGoToday"
          @jump-to-date="onJumpToDate"
        />
        <div class="head-actions">
          <button
            class="icon-btn"
            type="button"
            :disabled="loading"
            :title="loading ? '刷新中…' : '刷新好友的最新数据'"
            @click="refresh"
          >
            <FaIcon
              :icon="faArrowsRotate"
              :size="14"
              class="refresh-icon"
              :class="{ spinning: loading }"
            />
          </button>
          <span
            v-if="!canEdit"
            class="readonly-tip"
            title="好友的工作表仅可查看，无法编辑"
          >
            <FaIcon :icon="faEye" :size="12" />
            <span>只读</span>
          </span>
          <span
            v-else
            class="edit-tip"
            title="你已获得编辑授权，可直接增删改时间块"
          >
            <FaIcon :icon="faPenToSquare" :size="12" />
            <span>可编辑</span>
          </span>
        </div>
      </div>

      <!-- 「我的分类」跟随 EDIT 授权一起开放：拿到编辑授权的好友能直接维护这张表的分类，
           否则代填时间块时挑不到想要的分类。只读授权不渲染这一栏（好友页保持只展示时间表）。 -->
      <CategoryLegend
        v-if="canEdit"
        class="friend-legend"
        legend-title="好友的分类"
        :show-actions="false"
        :custom-categories="categories"
        @add-category="onAddCategory"
        @update-category="onUpdateCategory"
        @delete-category="onDeleteCategory"
      />

      <div ref="tableBody" class="table-body">
        <div v-if="loading" class="state-tip">加载中…</div>
        <div v-else-if="error" class="state-tip error">{{ error }}</div>
        <!-- 这一周没有时间块时不再提示文案，直接给一张空表 -->
        <TimeTable
          v-show="!loading && !error"
          :days="data.days"
          :events="data.events"
          :categories="categories"
          :readonly="!canEdit"
          :friend-owner-id="canEdit ? friendId : null"
          :project-id="projectId"
          :commentable="canEdit"
          hide-event-title
          :interactions="interactions"
          :active-block="activeBlockForTable"
          @select-event="onSelectEvent"
          @hover-event="onHoverEvent"
          @hover-leave="onHoverLeave"
          @update-status="onUpdateStatusLocal"
          @table-scroll="onTableScroll"
          @add-event="onAddEventLocal"
          @update-event="onUpdateEventLocal"
          @delete-event="onDeleteEventLocal"
        />
      </div>
    </div>

    <!-- 周累计表：只有好友明确开放了该权限（categoryVisible）才展示。
         默认不展示 —— 好友页的主内容是工作表本身，统计表属于额外开放的附加信息。
         ⚠️ 加载中 / 出错时这里也必须有占位文案：以前直接渲染空数据的 TimeStats，
            看起来像「表出来了但一条数据都没有」，而同一时刻时间表被挤没了，非常像 bug。
             两张表的加载态必须一致。 -->
    <div v-if="categoryVisible" class="friend-stats">
      <div v-if="loading" class="stats-state-tip">加载中…</div>
      <div v-else-if="error" class="stats-state-tip error">{{ error }}</div>
      <TimeStats
        v-else
        :days="data.days"
        :events="data.events"
        :categories="categories"
      />
    </div>

    <!-- 时间块评论气泡：鼠标在时间块上停留 0.5s 后贴在块右侧冒出来，4s 后自动淡出；
         鼠标移入气泡或点击时间块 → 固定住，不再自动消失，可正常看/写评论。 -->
    <BlockBubble
      ref="bubble"
      :visible="bubbleVisible"
      :block="bubbleBlock"
      :anchor="bubbleAnchor"
      :pinned="bubblePinned"
      :pinnable="!canEdit"
      resource-type="TIMEBLOCK"
      :title="bubbleBlock ? bubbleBlock.text : ''"
      :subtitle="bubbleBlock ? bubbleBlock.timeLabel : ''"
      @pointer-enter="onBubblePointerEnter"
      @pointer-leave="onBubblePointerLeave"
      @interacting="onBubbleInteracting"
      @changed="onBubbleChanged"
      @close="hideBubble"
    />
  </div>
</template>

<script>
import WeekNavigator from '@/components/list/WeekNavigator.vue'
import TimeTable from '@/components/list/TimeTable.vue'
import TimeStats from '@/components/list/TimeStats.vue'
import CategoryLegend from '@/components/list/CategoryLegend.vue'
import { minutesToLabel, nextBlockKey, DAY_MINUTES } from '@/components/list/constants'
import FaIcon from '@/components/common/FaIcon.vue'
import BlockBubble from './BlockBubble.vue'
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons/faArrowsRotate'
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons/faPenToSquare'
import { PROJECT_PALETTE } from '@/components/taskblock/CreateWorkspaceDialog.vue'
import {
  getFriendTimeBlocksRange,
  getProjectVersion,
  createFriendTimeBlock,
  updateFriendTimeBlock,
  createFriendTag,
  updateFriendTag,
  deleteFriendTag
} from '@/api/share.js'
import { getVisibleInteractionSummary } from '@/api/comment.js'
import { listFriendActivities } from '@/api/friend.js'

/**
 * 跨端同步版本号轮询间隔（毫秒）。
 *
 * 只查一个整数版本号（后端一条索引行），开销极小；发现版本变化才拉全量时间块。
 * 比好友列表动态的 60s 更短一些，让「好友/自己在其他端改表」能较快反映到当前页。
 */
const VERSION_POLL_MS = 20000

function getMonday(d) {
  const dt = new Date(d)
  const day = dt.getDay()
  const diff = (day === 0 ? -6 : 1) - day
  dt.setDate(dt.getDate() + diff)
  dt.setHours(0, 0, 0, 0)
  return dt
}

function fmtDate(d) {
  const m = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/**
 * 把「开始/结束时间」映射到当天的分钟坐标（0 ~ 1440），供表格定位使用。
 *
 * ⚠️ 不能只取 getHours()/getMinutes()：记录可能是**跨午夜**的
 * （例如 20:57 → 次日 00:00），那样结束时间会被算成 0 分，于是 end < start，
 * 时间块在表格里就渲染不出来了（切片条件 e > s 永不成立）。
 * 因此跨天时把结束时间统一收敛到当天 24:00（= 1440）。
 * ListPage 里有一份同样的实现，改动请同步。
 */
function toDayRange(startStr, endStr) {
  const s = new Date(startStr)
  if (!startStr || isNaN(s.getTime())) return { start: 0, end: 0 }
  const start = s.getHours() * 60 + s.getMinutes()
  const e = new Date(endStr)
  if (!endStr || isNaN(e.getTime())) return { start, end: start }
  const sameDay = e.getFullYear() === s.getFullYear() &&
                  e.getMonth() === s.getMonth() &&
                  e.getDate() === s.getDate()
  let end = sameDay ? e.getHours() * 60 + e.getMinutes() : 24 * 60
  if (end < start) end = start
  return { start, end }
}

/** 记录的真实时长（分钟）：按完整时间戳相减，跨午夜也正确。与 ListPage.durationMinutes 同实现。 */
function durationMinutes(startStr, endStr) {
  const s = new Date(startStr)
  const e = new Date(endStr)
  if (!startStr || !endStr || isNaN(s.getTime()) || isNaN(e.getTime())) return 0
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / 60000))
}

function emptyData(monday) {
  const days = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(d.getDate() + i)
    days.push({ date: fmtDate(d) })
  }
  return { days, events: [[], [], [], [], [], [], []] }
}

export default {
  name: 'FriendTablePanel',
  components: { WeekNavigator, TimeTable, TimeStats, CategoryLegend, FaIcon, BlockBubble },
  props: {
    friendId: {
      type: [String, Number],
      required: true
    },
    workspaceId: {
      type: [String, Number],
      required: true
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    /**
     * 好友工作表的主色（hex，存库字段）。
     * 与好友自己页面一致：把这张表的色系（10 种降饱和配色之一）派生成一整套
     * `--lp-*` 变量挂到时间表容器上，让好友看到的时间和好友本人改的色系一致，
     * 而不是永远落在 App.vue 的浅绿默认主题上。
     * 没传 / 色不在色板里 / 是「默认」色 时返回空对象，走 App.vue 的默认主题。
     */
    projectColor: {
      type: String,
      default: ''
    },
    tags: {
      type: Array,
      default: () => []
    },
    /**
     * 当前登录用户对该工作表是否拥有「编辑」授权。
     * true → 时间表可真正增删改（走好友协同编辑接口）；
     * false → 只读（点击时间块弹出评论气泡）。
     * 由 FriendDetailPanel 按 getFriendProjectPermission 结果传入。
     */
    editable: {
      type: Boolean,
      default: false
    },
    /**
     * 好友是否开放了该表的「分类（周累计）」统计表查看权限。
     * true → 时间表下方额外渲染 TimeStats；false（默认）→ 只展示工作表本身。
     * 由 FriendDetailPanel 按 getFriendProjectAccess 结果传入，与 editable 相互独立。
     */
    categoryVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      weekStart: getMonday(new Date()),
      data: emptyData(getMonday(new Date())),
      loading: false,
      error: '',
      // ===== 跨端同步 =====
      // 上次全量加载时拿到的版本号，作为轮询基线；轮询发现版本变化就静默重拉。
      version: null,
      // 本端刚编辑过好友表（增/改/删/状态流转/分类增改）：下次轮询检测到版本变化时跳过整表重拉，
      // 本地已有最新数据；不推进基线，编辑停下后的下一轮再正常同步（与 ListPage 一致）。
      justEdited: false,
      // 静默整表重拉是否在途：防止慢网络下版本轮询叠加并发重拉（与 ListPage 同源考量）。
      reloading: false,
      versionTimer: null,
      // ===== 时间块互动角标（评论数 / 点赞数）=====
      // 与「我的表格」页一致：好友时间表的时间块右下角也画「评论 / 点赞 图标 + 数字」，
      // 一眼看出哪些好友块有互动。结构同 ListPage：{ '<blockId>': { commentCount, likeCount } }
      interactions: {},
      interactionsToken: 0,
      // ===== 评论气泡状态 =====
      bubbleVisible: false,
      bubbleBlock: null,
      /** 源时间块的屏幕坐标，气泡按它贴在右侧 */
      bubbleAnchor: null,
      /** 是否已固定：固定后不再自动消失，也不再被悬停切走 */
      bubblePinned: false,
      /** 鼠标是否停在气泡里（停留期间不自动消失） */
      pointerInBubble: false,
      /** 当前评论面板对应的源时间块坐标，供 TimeTable 高亮显示 */
      bubbleDayIdx: -1,
      bubbleEventIndex: -1,
      /** 源时间块的 key：下标会因记录增删而错位，高亮优先认 key */
      bubbleEventKey: null,
      // 悬停 0.5s 才弹 / 4s 后自动淡出 / 移开后 240ms 收起
      hoverTimer: null,
      autoHideTimer: null,
      leaveTimer: null,
      faArrowsRotate,
      faEye,
      faPenToSquare
    }
  },
  computed: {
    categories() {
      return (this.tags || []).map(tag => ({
        key: String(tag.id),
        label: tag.name,
        color: tag.color
      }))
    },
    /**
     * 把好友工作表的主色（projectColor）派生为一整套 `--lp-*` 变量集合，
     * 挂在时间表容器上，让好友看到的时间表跟随好友本人设置的色系。
     *
     * 复用 owner 自己页面的同一套派生逻辑（TaskBlockPage.currentProjectThemeStyle）：
     * - 色不在 10 种新色板里（旧数据未迁移）→ 不覆盖，走 App.vue 默认主题；
     * - 选了「默认」色 → 同样不覆盖，走回浅绿默认主题；
     * - 覆盖主色 / 深主色 / 两级浅背景 / 两级边框 / 页面底色 / 次表面，
     *   与好友自己的表格页面保持一致。
     */
    themeStyle() {
      const color = this.projectColor
      if (!color) return {}
      const entry = PROJECT_PALETTE.find(c => c.active.toLowerCase() === color.toLowerCase())
      if (!entry || entry.isDefault) return {}
      return {
        '--lp-primary': entry.active,
        '--lp-primary-dark': entry.dark,
        '--lp-primary-soft': entry.soft,
        '--lp-primary-soft2': entry.soft2,
        '--lp-border': entry.border,
        '--lp-border-strong': entry.borderStrong,
        '--lp-bg': entry.bg,
        '--lp-surface-2': entry.surface2
      }
    },
    /** 是否可编辑：拿到 EDIT 授权才能动好友的时间块 */
    canEdit() {
      return !!this.editable
    },
    /** 当前评论面板指向的源时间块坐标，传给 TimeTable 做 .is-bubble-source 高亮；
     *  面板没开时返回 null，高亮自动撤销。 */
    activeBlockForTable() {
      if (!this.bubbleVisible || this.bubbleDayIdx < 0 || this.bubbleEventIndex < 0) return null
      return { dayIdx: this.bubbleDayIdx, index: this.bubbleEventIndex, key: this.bubbleEventKey }
    }
  },
  watch: {
    weekStart: {
      immediate: true,
      handler() {
        this.hideBubble()
        this.loadWeek()
      }
    },
    projectId() {
      this.hideBubble()
      this.loadWeek()
      // 切换工作表后，轮询目标随之改变，重启版本轮询
      this.startVersionPoll()
    }
  },
  mounted() {
    // 打开好友工作表后开始轮询版本号，实现「其他端改表 → 本页自动同步」
    this.startVersionPoll()
  },
  beforeDestroy() {
    // 组件销毁时把悬停/自动消失的计时一起清掉，避免回调打到已卸载的实例上
    this.clearTimers()
    this.stopVersionPoll()
  },
  methods: {
    /**
     * 加载当前显示的这一周。
     *
     * @param {boolean} forceRefresh 是否强制刷新（true 时后端清缓存回源）
     * @param {boolean} silent 静默刷新：不切换 loading 态、失败不清空现有表格。
     *        用于「版本号变化后自动同步」——避免整表闪一下「加载中…」。
     */
    async loadWeek(forceRefresh = false, silent = false) {
      if (!this.projectId || !this.workspaceId) return
      // 防并发重拉：版本轮询的 silent 重拉在途时，下一轮轮询若又触发会叠加请求；
      // 此处直接放弃本轮，交由在途的那次负责刷新（成功即推进基线，失败下轮重试）。
      if (this.reloading) return
      this.reloading = true
      if (!silent) {
        this.loading = true
        this.error = ''
      }
      const monday = this.weekStart
      const end = new Date(monday)
      end.setDate(end.getDate() + 6)
      try {
        const res = await getFriendTimeBlocksRange({
          userId: this.friendId,
          workspaceId: this.workspaceId,
          projectId: this.projectId,
          startDate: fmtDate(monday),
          endDate: fmtDate(end),
          refresh: !!forceRefresh
        })
        if (!res.success || res.code !== 200) {
          if (!silent) {
            this.error = res.message || '加载失败'
            this.data = emptyData(monday)
          }
          return
        }
        const items = (res.data && res.data.items) || {}
        const days = []
        const events = [[], [], [], [], [], [], []]
        for (let i = 0; i < 7; i++) {
          const d = new Date(monday)
          d.setDate(d.getDate() + i)
          const dateStr = fmtDate(d)
          days.push({ date: dateStr })
          events[i] = (items[dateStr] || []).map(item => this.convertItemToEvent(item))
        }
        this.data = { days, events }
        // 记录本次全量加载对应的版本号，作为后续轮询的基线
        if (res.data && res.data.version != null) {
          this.version = res.data.version
        }
        // 方案3：工作表响应已附带该好友实时状态，交给父组件刷新左侧动态行，
        //        无需再单独调一次 /friends/activities。
        this.emitActivity(res.data ? res.data.activity : null)
        // 拿到一周时间块后，批量拉取互动统计（评论 / 点赞数），供时间表右下角画角标
        this.loadInteractions()
      } catch (e) {
        if (!silent) {
          console.error('[FriendTablePanel] 加载周数据失败', e)
          this.error = '网络异常，请稍后重试'
          this.data = emptyData(monday)
        }
      } finally {
        this.reloading = false
        if (!silent) this.loading = false
      }
    },

    /** 把好友实时状态抛给父级（FriendDetailPanel → FriendsPage 更新左侧动态行）。 */
    emitActivity(activity) {
      this.$emit('activity-changed', {
        friendId: this.friendId,
        activity: activity || null
      })
    },
    /**
     * 本地状态流转（结束在做 / 新增在做 / 编辑或删除在做块）后，立刻把好友的实时状态
     * 推给父组件（左栏动态行 + 右侧详情面板），否则要等 60s 轮询才刷新，看着像"卡在做状态"。
     * 好友的实时状态是全局的（可能跨多个工作表），不能本地派生，只能回源拉取。
     */
    async refreshFriendActivity() {
      try {
        const res = await listFriendActivities()
        const list = (res && res.success && res.data) ? res.data : []
        const found = list.find(a => a && String(a.friendId) === String(this.friendId))
        this.$emit('activity-changed', { friendId: this.friendId, activity: found || null })
      } catch (e) {
        // 拉取失败：保留上一次动态，不回退、不报错打扰主流程
        console.warn('[FriendTablePanel] 刷新好友实时状态失败', e)
      }
    },

    /** 开始（或重启）版本号轮询。同一时刻只保留一个计时器。 */
    startVersionPoll() {
      this.stopVersionPoll()
      if (!this.projectId) return
      this.versionTimer = setInterval(this.checkVersion, VERSION_POLL_MS)
    },

    /** 停止版本号轮询。 */
    stopVersionPoll() {
      if (this.versionTimer) {
        clearInterval(this.versionTimer)
        this.versionTimer = null
      }
    },

    /**
     * 轮询版本号：只取一个整数，与基线比较。
     * 变化 → 说明其他端改动了这张表 → 静默重拉（带 refresh 绕过好友表缓存）。
     * 首次拿到基线（version 为 null）时只记录、不触发重拉，避免刚打开就重复请求。
     */
    async checkVersion() {
      if (!this.projectId || this.loading) return
      try {
        const res = await getProjectVersion(this.projectId)
        if (!res || !res.success || res.code !== 200) return
        const latest = res.data ? res.data.version : null
        if (latest == null) return
        if (this.version == null) {
          this.version = latest
          return
        }
        if (String(latest) !== String(this.version)) {
          if (this.justEdited) {
            // 本端刚编辑过好友表：本地已有最新数据，本轮跳过整表静默重拉，
            // 且不推进基线 —— 编辑停下后的下一轮轮询会正常整表重拉，
            // 既能合并连续编辑期间的多次版本变更，又能兜住他端改动（与时间线页一致）。
            this.justEdited = false
            return
          }
          // 已有静默重拉在途则跳过本轮，避免慢网络下下一轮轮询叠加并发重拉
          if (this.reloading) return
          // 延迟推进基线：由 loadWeek 成功把版本号写回后再更新，
          // 避免「基线已推进但拉取失败」导致漏同步（与 ListPage 一致）。
          await this.loadWeek(true, true)
        }
      } catch (e) {
        // 轮询失败静默忽略，下个周期再试（不打扰用户）
      }
    },
    refresh() {
      if (this.loading) return
      // 强制刷新：让后端清掉缓存回源查库，立刻拿到好友的最新数据
      this.hideBubble()
      this.loadWeek(true)
    },

    /**
     * 批量拉取整周时间块的互动统计（评论数 / 点赞数），供时间表右下角画角标。
     * 与「我的表格」页 ListPage.loadInteractions 同源逻辑：一次请求覆盖整周，
     * 绝不按块逐个请求；用 token 丢弃过期响应，避免切周 / 刷新时的竞态覆盖。
     * 这里走「可见资源」统计接口（getVisibleInteractionSummary），
     * 因为好友时间块的所有者是好友本人，按 owner_id == 当前用户 过滤会漏掉全部计数。
     */
    async loadInteractions() {
      const ids = [...new Set(
        (this.data.events || [])
          .reduce((all, dayEvents) => all.concat(dayEvents || []), [])
          .map(evt => evt && evt.id)
          .filter(Boolean)
      )]
      const token = ++this.interactionsToken
      if (!ids.length) {
        this.interactions = {}
        return
      }
      try {
        const res = await getVisibleInteractionSummary({ resourceType: 'TIMEBLOCK', resourceIds: ids })
        if (token !== this.interactionsToken) return
        if (res && res.success && res.code === 200) {
          this.interactions = res.data || {}
        }
      } catch (e) {
        console.warn('[FriendTablePanel] 拉取时间块互动统计失败', e)
      }
    },

    /**
     * 把「某天的分钟坐标」转成 ISO 时间串，复用 TimeTable 的拼接规则
     * （yyyy-MM-ddTHH:mm:00，本地时间、不带时区）。
     */
    buildDateTime(dateStr, minutes) {
      const [y, M, d] = dateStr.split('-').map(Number)
      const hh = String(Math.floor(minutes / 60)).padStart(2, '0')
      const mm = String(minutes % 60).padStart(2, '0')
      return `${y}-${String(M).padStart(2, '0')}-${String(d).padStart(2, '0')}T${hh}:${mm}:00`
    },
    /** 取分类颜色（用于写入时间块配色），无分类返回 null */
    categoryColor(cat) {
      if (!cat || cat === 'none') return null
      const found = this.categories.find(c => c.key === String(cat))
      return found ? found.color : null
    },
    /**
     * 组装好友协同编辑接口需要的请求体。
     *
     * ⚠️ kind 决定 actual 取值与执行状态（与 QuickAddModal.createRecord /
     *    移动端 buildBlockBody 同口径）：
     *    - done：actual = planned，状态 COMPLETED
     *    - running：只有 actualStart，actualEnd 留空，状态 RUNNING
     *    - planned：actual 全空，状态 PLANNED
     *    缺了这段，好友代建 / 代改的「在做 / 计划」块会被写成 actual 起止都有，
     *    渲染层按 actual 推断 kind 后又全部退化成实线普通块。
     */
    buildBlockBody(dayIdx, start, end, cat, text, kind) {
      const dateStr = this.data.days[dayIdx] && this.data.days[dayIdx].date
      const startISO = this.buildDateTime(dateStr, start)
      const endISO = this.buildDateTime(dateStr, end)
      const k = kind || 'done'
      const isPlanned = k === 'planned'
      const isRunning = k === 'running'
      return {
        workspaceId: this.workspaceId,
        projectId: this.projectId,
        tagId: cat && cat !== 'none' ? cat : null,
        blockType: 'RECORD',
        executionStatus: isPlanned ? 'PLANNED' : (isRunning ? 'RUNNING' : 'COMPLETED'),
        plannedStartTime: startISO,
        plannedEndTime: endISO,
        actualStartTime: isPlanned ? null : startISO,
        actualEndTime: isRunning ? null : endISO,
        content: text || '',
        color: this.categoryColor(cat),
        note: ''
      }
    },
    /**
     * 新增时间块（好友编辑权限）：与「我的表格」页一致——把服务端返回的新块直接并入本地，
     * 不再整周重拉（避免无谓的整表刷新 + 互动统计二次拉取）。
     *
     * ⚠️ 已带 id 的块（跨天结束「在做」时由 endRunningBlock 落库后再 emit 过来）
     *    直接并本地即可，绝不再发一次创建请求 —— 否则同一条记录会被建两遍。
     */
    async onAddEventLocal(payload) {
      if (!this.canEdit) return
      // 已落库的块（跨天拆块 / 其它带回 id 的来源）：只并本地，不重复创建
      if (payload.id) {
        const evt = this.convertItemToEvent({
          id: payload.id,
          tagId: payload.cat && payload.cat !== 'none' ? payload.cat : null,
          content: payload.text || '',
          color: this.categoryColor(payload.cat) || '',
          actualStartTime: payload.actualStartTime || null,
          actualEndTime: payload.actualEndTime || null,
          plannedStartTime: payload.plannedStartTime || null,
          plannedEndTime: payload.plannedEndTime || null
        })
        const events = this.data.events[payload.dayIdx] || []
        events.push(evt)
        this.$set(this.data.events, payload.dayIdx, events)
        return
      }
      try {
        this.justEdited = true
        const res = await createFriendTimeBlock(this.friendId, this.buildBlockBody(payload.dayIdx, payload.start, payload.end, payload.cat, payload.text, payload.kind))
        if (!res.success || res.code !== 200) {
          this.$message.error(res.message || '新增失败')
          return
        }
        const item = res.data
        // 兜底：拿不到服务端返回的块（理论不会出现），整周重拉保证不丢
        if (!item || !item.id) {
          await this.loadWeek(true)
          return
        }
        // 服务端回传已带 actual/planned 四个时间戳，convertItemToEvent 据此派生 kind；
        // 个别字段缺失时用弹窗 payload 兜底，避免新建的「在做 / 计划」块退化成实线普通块
        const evt = this.convertItemToEvent({
          ...item,
          actualStartTime: item.actualStartTime || payload.actualStartTime || null,
          actualEndTime: item.actualEndTime || payload.actualEndTime || null,
          plannedStartTime: item.plannedStartTime || payload.plannedStartTime || null,
          plannedEndTime: item.plannedEndTime || payload.plannedEndTime || null
        })
        const events = this.data.events[payload.dayIdx] || []
        events.push(evt)
        this.$set(this.data.events, payload.dayIdx, events)
        // 新增「在做」块：立即刷新好友实时状态卡片，让"在做"状态马上出现
        if (payload.kind === 'running') this.refreshFriendActivity()
      } catch (e) {
        console.error('[FriendTablePanel] 好友新增时间块失败', e)
        this.$message.error('新增失败，请稍后重试')
      }
    },
    /**
     * 修改时间块（好友编辑权限）：只把对应块在本地原地更新，不再整周重拉。
     * ⚠️ 优先按 key 定位：冲突处理可能已经删掉过其它记录，下标不再可信。
     * ⚠️ kind / duration / 四个原始时间戳必须保留或透传：编辑弹窗里类型固定为当前 kind，
     *    丢了会让「在做 / 计划」块在本地退化成实线普通块（渲染层按 kind 区分样式），
     *    之后点「结束 / 勾选完成」也会因取不到 actual 而派生失败。
     */
    async onUpdateEventLocal(payload) {
      if (!this.canEdit) return
      const dayEvents = this.data.events[payload.dayIdx] || []
      let i = -1
      if (payload.key != null) i = dayEvents.findIndex(e => e && String(e.key) === String(payload.key))
      if (i < 0 && typeof payload.index === 'number') i = payload.index
      const existing = i >= 0 ? dayEvents[i] : null
      if (!existing || !existing.id) return
      try {
        this.justEdited = true
        const res = await updateFriendTimeBlock(this.friendId, existing.id, this.buildBlockBody(payload.dayIdx, payload.start, payload.end, payload.cat, payload.text, payload.kind || existing.kind))
        if (!res.success || res.code !== 200) {
          this.$message.error(res.message || '修改失败')
          return
        }
        const updated = {
          id: existing.id,
          // key 必须原样保留：它是这条记录在数组里的"身份证"，换了就等于新增一条
          key: existing.key,
          start: payload.start,
          end: payload.end,
          cat: payload.cat,
          text: payload.text || '',
          color: this.categoryColor(payload.cat) || existing.color || '',
          // 保留原有状态：编辑弹窗里类型固定为当前 kind，不会跨状态切换
          kind: existing.kind || payload.kind || 'done',
          // 时长沿用原值（跨午夜的记录 end 会被收敛到 1440，end-start 不是真实时长）；
          // 时间被改动过、或原本就没有 duration 时，按当天分钟坐标重算
          duration: (existing.duration && existing.start === payload.start && existing.end === payload.end)
            ? existing.duration
            : Math.max(0, payload.end - payload.start),
          // 原始时间戳一并保留：状态流转后需要据此重新派生
          actualStartTime: existing.actualStartTime || null,
          actualEndTime: existing.actualEndTime || null,
          plannedStartTime: existing.plannedStartTime || null,
          plannedEndTime: existing.plannedEndTime || null
        }
        dayEvents.splice(i, 1, updated)
        this.$set(this.data.events, payload.dayIdx, dayEvents)
        // 「在做」块被编辑：立即刷新好友实时状态卡片，让卡片内容同步
        if (existing.kind === 'running' || updated.kind === 'running') this.refreshFriendActivity()
      } catch (e) {
        console.error('[FriendTablePanel] 好友修改时间块失败', e)
        this.$message.error('修改失败，请稍后重试')
      }
    },
    /**
     * 删除时间块：TimeTable 已经（以好友身份）调过好友删除接口并 emit delete-event，
     * 这里只需把对应块从本地移除即可，不要再重复发删除请求、也不要整周重拉。
     */
    onDeleteEventLocal(payload) {
      if (!this.canEdit) return
      this.justEdited = true
      const dayEvents = this.data.events[payload.dayIdx] || []
      let i = -1
      if (payload.key != null) i = dayEvents.findIndex(e => e && String(e.key) === String(payload.key))
      if (i < 0 && typeof payload.index === 'number') i = payload.index
      if (i < 0) return
      const removed = dayEvents[i]
      dayEvents.splice(i, 1)
      this.$set(this.data.events, payload.dayIdx, dayEvents)
      // 删掉的若是「在做」块：立即刷新好友实时状态卡片，避免"在做"状态残留
      if (removed && removed.kind === 'running') this.refreshFriendActivity()
    },

    /**
     * 时间块状态流转（计划→完成 / 在做→结束）后，按新状态重新派生本地渲染字段。
     *
     * 由 TimeTable 调好友状态接口成功后 emit 过来；这里只动本地数据，不重复发请求。
     * kind 完全由 actual 时间戳派生，所以只要把新的 actualStartTime/actualEndTime 写回，
     * 再跑一遍 deriveEventFields 即可 —— 否则结束 / 勾选完成后界面仍显示旧状态，
     * 要等手动刷新才会变。
     * 口径与 ListPage.onUpdateStatus 一致。
     */
    onUpdateStatusLocal({ dayIdx, key, actualStartTime, actualEndTime }) {
      this.justEdited = true
      const dayEvents = this.data.events[dayIdx] || []
      let i = -1
      if (key != null) i = dayEvents.findIndex(e => e && String(e.key) === String(key))
      if (i < 0) return
      const existing = dayEvents[i]
      const updated = {
        ...existing,
        actualStartTime: actualStartTime || existing.actualStartTime || null,
        actualEndTime: actualEndTime || existing.actualEndTime || null
      }
      Object.assign(updated, this.deriveEventFields(updated))
      dayEvents.splice(i, 1, updated)
      this.$set(this.data.events, dayIdx, dayEvents)
      // 状态流转（含「结束在做」）会改写好友的实时状态，立即刷新卡片，避免等 60s 轮询
      this.refreshFriendActivity()
    },

    // ===== 好友分类（「我的分类」跟随 EDIT 授权一起开放） =====
    /**
     * 三个写操作都是「先调好友分类接口，再让父组件重新拉一次分类列表」：
     * 分类数据由 FriendDetailPanel 持有（时间块渲染也要用），这里只负责发请求并通知它刷新，
     * 不本地改 props，避免父子各存一份导致不一致。
     */
    async onAddCategory({ name, color }) {
      if (!this.canEdit) return
      this.justEdited = true
      try {
        // name 里已含图标前缀（'fa:book学习' / '🍕吃饭'），icon 字段留空，与自己的表格页一致
        const res = await createFriendTag(this.friendId, {
          projectId: this.projectId,
          name,
          color,
          icon: ''
        })
        if (!res.success || res.code !== 200) {
          this.$message.error(res.message || '新增分类失败')
          return
        }
        this.$emit('tags-changed')
      } catch (e) {
        console.error('[FriendTablePanel] 好友新增分类失败', e)
        this.$message.error('新增分类失败，请稍后重试')
      }
    },
    async onUpdateCategory({ key, name, color }) {
      if (!this.canEdit) return
      this.justEdited = true
      try {
        const res = await updateFriendTag(this.friendId, key, { name, color, icon: '' })
        if (!res.success || res.code !== 200) {
          this.$message.error(res.message || '修改分类失败')
          return
        }
        this.$emit('tags-changed')
      } catch (e) {
        console.error('[FriendTablePanel] 好友修改分类失败', e)
        this.$message.error('修改分类失败，请稍后重试')
      }
    },
    async onDeleteCategory(key) {
      if (!this.canEdit) return
      // CategoryLegend 现在只负责发请求，确认框统一由父组件弹（见 ListPage 的两条分支）
      try {
        await this.$confirm('确定删除该分类吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      try {
        const res = await deleteFriendTag(this.friendId, key)
        if (!res.success || res.code !== 200) {
          this.$message.error(res.message || '删除分类失败')
          return
        }
        this.$emit('tags-changed')
        // 删掉的分类可能正被某些时间块引用，重拉一周让它们立刻落回「无分类」
        await this.loadWeek(true)
      } catch (e) {
        console.error('[FriendTablePanel] 好友删除分类失败', e)
        this.$message.error('删除分类失败，请稍后重试')
      }
    },
    /**
     * 把后端下发的 item 转成 TimeTable 的事件对象。
     *
     * ⚠️ 必须派生 kind / duration 并带上四个原始时间戳：渲染层完全按 kind 区分
     *    「普通（实线）/ 在做（渐变 + 徽标）/ 计划（虚线 + 徽标）」三种样式，
     *    缺 kind 的块会一律退化成 done 实线块 —— 用户看到的就是
     *    「好友页看不到在做或计划的任务，全部都是实线」。
     *    口径与 ListPage.convertItemToEvent / 移动端 TimeBlockEvent.fromItem 完全一致。
     */
    convertItemToEvent(item) {
      const cat = item.tagId ? String(item.tagId) : 'none'
      const derived = this.deriveEventFields(item)
      return {
        id: item.id,
        // 稳定标识：增删改按它定位，下标会因数组增删而失效
        key: nextBlockKey(),
        cat,
        text: item.content || '',
        color: item.color || '',
        // 原始时间戳一并带上：状态流转（计划→完成 / 在做→结束）后，
        // 需要据此重新派生 start/end/kind，否则本地数据不会跟着变。
        actualStartTime: item.actualStartTime || null,
        actualEndTime: item.actualEndTime || null,
        plannedStartTime: item.plannedStartTime || null,
        plannedEndTime: item.plannedEndTime || null,
        ...derived
      }
    },
    /**
     * 从原始时间戳派生渲染所需字段，并推断时间块类型（kind）：
     * - 实际开始 + 实际结束都有 → 'done'（普通已完成）
     * - 只有实际开始 → 'running'（正在做，填充「从开始到下一个时间块」这一段）
     * - 都没有 → 'planned'（预设）
     *
     * ⚠️ 完全基于 actual 字段推断，不依赖后端 executionStatus 字符串，
     *    这样旧数据（状态写的是 PLANNED 但 actual 已填）也能正确识别成普通块。
     *    与 ListPage.deriveEventFields 同口径，改动请同步。
     */
    deriveEventFields(item) {
      const hasActualStart = !!item.actualStartTime
      const hasActualEnd = !!item.actualEndTime
      let kind = 'done'
      if (!hasActualStart && !hasActualEnd) kind = 'planned'
      else if (hasActualStart && !hasActualEnd) kind = 'running'

      const startStr = item.actualStartTime || item.plannedStartTime
      const endStr = item.actualEndTime || item.plannedEndTime
      const { start, end } = toDayRange(startStr, endStr)
      // 正在做：结束时间还没发生，块要占位到「下一个时间块的前一分钟」——
      // 这个边界在创建时已经写进 plannedEndTime（= 当时所在空档的末尾），直接拿来用即可；
      // 只有老数据（plannedEnd 缺失 / 比开始还早）才退回「铺到当天结束」的旧行为。
      if (kind === 'running') {
        return { start, end: end > start ? end : DAY_MINUTES, duration: 0, kind }
      }
      return {
        start,
        end,
        // 真实时长：按完整时间戳算，跨午夜的记录不会被算成负数
        duration: hasActualStart && hasActualEnd ? durationMinutes(startStr, endStr) : 0,
        kind
      }
    },

    // ===== 时间块评论气泡 =====
    /**
     * 悬停进入某个时间块：先清掉一切待执行的动作，再起一个 0.5s 的停顿计时。
     * 只有鼠标真的在块上停留够 0.5s 才弹气泡 —— 掠过、误触都不会切换目标，
     * 也避免鼠标划过整张表时气泡一路乱跳。
     */
    onHoverEvent(payload) {
      if (!payload || !payload.event || !payload.event.id) return
      // 已固定的气泡不被悬停切走
      if (this.bubblePinned) return
      // 跨小时行的时间块被切成多段：在同块的段之间移动不算换目标，
      // 只把锚点挪到当前这段上并续上自动消失的计时，气泡不会闪一下重开。
      if (this.bubbleVisible && this.bubbleBlock &&
          String(this.bubbleBlock.id) === String(payload.event.id)) {
        this.bubbleAnchor = payload.rect || this.bubbleAnchor
        this.startAutoHide()
        return
      }
      this.clearTimers()
      // 换块时先收起旧气泡：旧气泡消失 → 新块停留满 0.5s → 再出现，绝不会瞬间跳位
      if (this.bubbleVisible) this.resetBubble()
      const pending = payload
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null
        this.openBubble(pending, false)
      }, 500)
    },
    /** 鼠标离开时间块：撤掉还没弹出的计时；已经弹出的给一点宽限，方便移进气泡。 */
    onHoverLeave() {
      if (this.bubblePinned) return
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }
      if (!this.bubbleVisible) return
      clearTimeout(this.leaveTimer)
      this.leaveTimer = setTimeout(() => {
        if (this.bubblePinned || this.pointerInBubble) return
        this.hideBubble()
      }, 240)
    },
    /** 点击时间块 = 明确想评论这一块：立刻弹出并固定，不再自动消失。 */
    onSelectEvent(payload) {
      if (!payload || !payload.event || !payload.event.id) return
      this.clearTimers()
      this.openBubble(payload, true)
    },
    openBubble(payload, pinned) {
      const evt = payload.event
      this.bubbleBlock = {
        id: evt.id,
        text: evt.text || '时间块',
        color: evt.color || '',
        timeLabel: `${minutesToLabel(evt.start)}-${minutesToLabel(evt.end)}`
      }
      this.bubbleDayIdx = payload.dayIdx != null ? payload.dayIdx : -1
      this.bubbleEventIndex = payload.index != null ? payload.index : -1
      this.bubbleEventKey = evt ? evt.key : null
      this.bubbleAnchor = payload.rect || null
      this.bubblePinned = !!pinned
      this.bubbleVisible = true
      // 纯悬停预览：存在 4s 后自动淡出
      if (!pinned) this.startAutoHide()
    },
    startAutoHide() {
      clearTimeout(this.autoHideTimer)
      this.autoHideTimer = setTimeout(() => {
        this.autoHideTimer = null
        if (this.bubblePinned || this.pointerInBubble) return
        this.hideBubble()
      }, 4000)
    },
    /**
     * 焦点进到气泡里（用户点进评论输入框开始写）→ **把气泡升级成固定态**。
     *
     * ⚠️ 与 ListPage 同一个坑：未固定的气泡在鼠标移出 300ms 后就收起，
     *    输入框一销毁焦点就掉回 body，接着按空格变成浏览器翻页（页面下滑），
     *    用户看到的就是「气泡打不了字，一按空格就退出」。
     *    固定后只有点关闭 / 点气泡外 / 切周 / 刷新才会收起。
     */
    onBubbleInteracting(active) {
      if (!active) return
      this.clearTimers()
      this.bubblePinned = true
    },
    /**
     * 气泡里的评论 / 点赞 / 取消点赞发生后：重新拉一次互动统计，
     * 让时间块右下角的角标数字立刻跟着变（与 ListPage 一致）。
     */
    onBubbleChanged() {
      this.loadInteractions()
    },
    /** 鼠标移入气泡：视为用户想看/想写，取消自动消失与收起计时。 */
    onBubblePointerEnter() {
      this.pointerInBubble = true
      clearTimeout(this.autoHideTimer)
      this.autoHideTimer = null
      clearTimeout(this.leaveTimer)
      this.leaveTimer = null
    },
    /** 鼠标移出气泡：给 300ms 宽限，回不去就收起（固定态除外）。 */
    onBubblePointerLeave() {
      this.pointerInBubble = false
      if (this.bubblePinned) return
      clearTimeout(this.leaveTimer)
      this.leaveTimer = setTimeout(() => {
        if (this.bubblePinned || this.pointerInBubble) return
        this.hideBubble()
      }, 300)
    },
    clearTimers() {
      if (this.hoverTimer) { clearTimeout(this.hoverTimer); this.hoverTimer = null }
      if (this.autoHideTimer) { clearTimeout(this.autoHideTimer); this.autoHideTimer = null }
      if (this.leaveTimer) { clearTimeout(this.leaveTimer); this.leaveTimer = null }
    },
    /** 只清数据不撤计时器，用于换块时的瞬间收起 */
    resetBubble() {
      this.bubbleVisible = false
      this.bubbleBlock = null
      this.bubbleAnchor = null
      this.bubbleDayIdx = -1
      this.bubbleEventIndex = -1
      this.bubbleEventKey = null
    },
    hideBubble() {
      this.clearTimers()
      this.resetBubble()
      this.bubblePinned = false
      this.pointerInBubble = false
    },
    /**
     * 点击表格滚动后关闭面板，避免源时间块滚出视野后失去上下文。
     *
     * ⚠️ 已固定的气泡（用户正在写评论）不关：滚一下就把写了半天的输入框收掉太粗暴，
     *    且焦点一丢，紧接着的空格会变成浏览器翻页。
     */
    onTableScroll() {
      if (this.bubbleVisible && !this.bubblePinned) this.hideBubble()
    },
    onChangeWeek(offsetDays) {
      const d = new Date(this.weekStart)
      d.setDate(d.getDate() + offsetDays)
      this.weekStart = d
    },
    onGoToday() {
      this.weekStart = getMonday(new Date())
    },
    onJumpToDate(dateStr) {
      const [y, m, d] = dateStr.split('-').map(Number)
      this.weekStart = getMonday(new Date(y, m - 1, d))
    }
  }
}
</script>

<style scoped>
/* 面板整体是「兜底滚动容器」：
   时间表 + 周累计表在高度不够时加起来放不下，就让本面板滚动，
   而不是把时间表压扁（压扁后周累计表看着像"盖住"了时间表）。
   ⚠️ 用 overflow-y:auto 不会影响 BlockBubble 的 position:fixed ——
      overflow 不会给 fixed 元素建立包含块（只有 transform/filter/perspective 会），
      气泡依旧相对视口定位、也不会被裁掉。 */
.friend-table-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  gap: 10px;
  overflow-y: auto;
  /* 隐藏面板自身的滚动条：时间表内部的 .tablebox 滚动条已在 TimeTable 里隐藏，
     这里把面板这层也隐藏，避免「好友表」右侧出现可见滚轴（与好友自己的「时间表」页一致）。
     滚动仍可用——滚轮 / 触控板 / 拖动页面即可，只是不再画那根条。 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.friend-table-panel::-webkit-scrollbar {
  display: none;
}

/* 时间表视图（周导航 + 好友的分类 + 时间表）：
   给它 min-height:100% —— 它一定占满面板的可见高度，时间表因此拿到全部可用高度，
   不会被下方的周累计表挤扁；周累计表则被顶到折线以下，必须向下滑动才能看到。
   观感与用户自己的「时间表」页一致：上面一张大表，往下滑才是周累计表。 */
.table-view {
  flex: 0 0 auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #91a67b;
  background: #edf2d5;
  color: #57795c;
  border-radius: 10px;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.icon-btn:hover:not(:disabled) {
  background: #e2ecc4;
  color: #35512d;
}

.icon-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-icon.spinning {
  animation: friend-refresh-spin 0.8s linear infinite;
}

@keyframes friend-refresh-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.readonly-tip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #57795c;
  background: #edf2d5;
  border: 1px solid #91a67b;
  border-radius: 10px;
  padding: 5px 10px;
  white-space: nowrap;
  cursor: default;
}

.edit-tip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #2f7a4a;
  background: #e9f5ec;
  border: 1px solid #4aa46a;
  border-radius: 10px;
  padding: 5px 10px;
  white-space: nowrap;
  cursor: default;
}

.table-body {
  /* ⚠️ flex-basis 必须是 0（不能是 auto）：
     .table-view 的高度是「auto 内容高」与「min-height:100%」取大者。
     若这里写 flex:1 1 auto，flex base size = 时间表内容高（24 行 ≈ 1049px），
     会把 .table-view 的内容高顶到 1100+，min-height:100% 就形同虚设 ——
     整张表全展开、表格自己不再滚动（表头 sticky 失效），滚动条跑到面板上。
     写成 flex:1 1 0 后，本项的「假想尺寸」由 min-height 兜底（420px），
     .table-view 的内容高 ≈ 周导航 + 分类图例 + 420 ≈ 516；
     面板够高时 .table-view 取 100%，多出来的空间再由 flex-grow 分给这里，
     时间表因此拿满一屏高度且**自己内部滚动**（表头保持 sticky）。 */
  flex: 1 1 0;
  min-height: 420px;
  /* 关键：这里不做滚动容器，滚动交给内部 TimeTable 的 .tablebox。 */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 让 TimeTable 撑满 .table-body 并把滚动收在自己身上：
   覆盖它默认的 min-height:420px，避免容器比可视区还高导致内容被裁
   （高度下限已经由 .table-body 的 min-height 保证）。 */
.table-body >>> .tablebox {
  flex: 1 1 auto;
  min-height: 0;
}

/* 周累计表（好友开放了该权限才渲染）：
   按内容自然高度展示，不设 max-height、也不自己做滚动 ——
   它被 .table-view 顶到折线以下，想看就得向下滑；
   高度不够时统一交给 .friend-table-panel 滚动，避免出现「面板 / 表 / 周累计表」三层滚动条。 */
.friend-stats {
  flex: 0 0 auto;
}
/* 间距统一交给父容器的 gap:10px，去掉组件自带的 margin-top */
.friend-stats >>> .stats-wrap {
  margin-top: 0;
}
/* 周累计表的加载 / 出错占位：与时间表 .state-tip 的观感保持一致 */
.stats-state-tip {
  padding: 18px 0;
  text-align: center;
  font-size: 14px;
  color: var(--lp-text-3, #94a59b);
}
.stats-state-tip.error {
  color: #d9534f;
}

.state-tip {
  padding: 40px 0;
  text-align: center;
  color: #8a9a78;
  font-size: 14px;
}

.state-tip.error {
  color: #c45c5c;
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸与间距全部沿用上面的原值。 */
/* 刷新图标按钮 */
.icon-btn{
  background:transparent;
  border:none;
  border-radius:0.625rem;
  color:var(--lp-text-3,#94a59b);
}
.icon-btn:hover:not(:disabled){
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
/* 只读标签 */
.readonly-tip{
  background:var(--lp-surface-2,#f7fbf8);
  border-color:var(--lp-border,#e3efe6);
  color:var(--lp-text-2,#5f7268);
  border-radius:0.5rem;
}
/* 可编辑标签 */
.edit-tip{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
  color:var(--lp-primary-dark,#2f7a4a);
  border-radius:0.5rem;
}
/* 状态提示 */
.state-tip{
  color:var(--lp-text-3,#94a59b);
}
.state-tip.error{
  color:#d9534f;
}
/* 「好友的分类」图例（拿到 EDIT 授权才渲染）：
   间距统一交给父容器的 gap:10px，去掉组件自带的 margin-bottom，避免上下不对称。
   选择器写成 `.legend.legend` 是为了把优先级抬到子组件 scoped 的 `.legend` 之上
   （两者原本都是 (0,2,0)，谁生效取决于打包顺序）。 */
.friend-table-panel >>> .legend.legend{
  flex-shrink:0;
  margin-bottom:0;
}
</style>
